/**
 * Profiles BFF API client.
 *
 * Typed wrapper over :func:`apiFetch` for the ``/api/profiles``
 * surface (ADR-033 §4.1). Request/response shapes come from the
 * generated ``api-types.gen.ts`` (regenerated by ``npm run
 * codegen`` from ``packages/meridian_bff/api/openapi.yaml``) — the
 * UI never hand-rolls these (CLAUDE.md §5.11).
 *
 * Increment 4a wires only the **read** surface (``listProfiles``,
 * ``getProfile``). The mutating functions
 * (``createProfile``/``updateProfile``/``deleteProfile``/``resetProfile``)
 * are defined here so later increments — the editor (increment 8),
 * import (increment 9), and the admin reset affordance — have a
 * single typed home, but they are not invoked from any UI surface
 * in this slice.
 *
 * Cross-references:
 *   - REQ-AUTH-065 (list = globals + own user Profiles).
 *   - REQ-AUTH-066 (single-Profile read; owner gating server-side).
 *   - REQ-AUTH-067 / 068 / 069 / 071 (create / update / delete /
 *     reset — wired in later increments).
 *   - REQ-UI-035 (BFF-only — every call goes through ``apiFetch``).
 *   - ADR-033 §4.1.
 */
import { apiFetch } from "@/lib/api-client";
import type { ProfileDetail, ProfileSummary } from "@/auth/session-types";
import type { components } from "@/lib/api-types.gen";

/** Body of ``POST /api/profiles`` (generated). */
export type ProfileCreateRequest = components["schemas"]["ProfileCreateRequest"];

/** Body of ``PUT /api/profiles/{id}`` (generated). */
export type ProfileUpdateRequest = components["schemas"]["ProfileUpdateRequest"];

/**
 * List the Profiles visible to the caller: every Global Profile
 * plus the caller's own User Profiles, metadata only (no
 * documents). Mirrors the bootstrap ``profiles`` field — used by
 * :func:`ProfilesProvider.refresh` to re-sync after a mutation.
 *
 * REQ-AUTH-065; ADR-033 §4.1.
 */
export async function listProfiles(): Promise<ProfileSummary[]> {
  return apiFetch<ProfileSummary[]>("/api/profiles");
}

/**
 * Fetch a single Profile's full, forward-migrated document plus
 * metadata. Global Profiles are readable by any session; a User
 * Profile only by its owner (the BFF returns 404 otherwise, to
 * avoid existence disclosure — REQ-AUTH-066). Used by Apply,
 * Edit, and Export to obtain the document on demand.
 *
 * REQ-AUTH-066; ADR-033 §4.1.
 */
export async function getProfile(profileId: string): Promise<ProfileDetail> {
  return apiFetch<ProfileDetail>(`/api/profiles/${encodeURIComponent(profileId)}`);
}

/**
 * Create a Profile after server-side strict validation. Creating a
 * Global Profile requires the ``admin`` role; a User Profile forces
 * ownership to the caller; a name collision within the scope
 * returns HTTP 409 (``ApiError`` with ``status === 409``).
 *
 * Defined for the editor (increment 8) and import (increment 9);
 * **not wired in increment 4a.**
 *
 * REQ-AUTH-067; ADR-033 §4.1.
 */
export async function createProfile(body: ProfileCreateRequest): Promise<ProfileDetail> {
  return apiFetch<ProfileDetail>("/api/profiles", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * Update a Profile's name and/or document. Global Profiles are
 * mutable only by ``admin``; a User Profile only by its owner;
 * built-in Profiles stay ``is_builtin`` and remain admin-editable;
 * name collisions return HTTP 409.
 *
 * Defined for the editor (increment 8); **not wired in increment
 * 4a.**
 *
 * REQ-AUTH-068; ADR-033 §4.1.
 */
export async function updateProfile(
  profileId: string,
  body: ProfileUpdateRequest,
): Promise<ProfileDetail> {
  return apiFetch<ProfileDetail>(`/api/profiles/${encodeURIComponent(profileId)}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

/**
 * Delete a Profile. A Global Profile is deletable only by
 * ``admin``; a User Profile only by its owner; a built-in Profile
 * cannot be deleted (HTTP 409). Deletion does not alter any user's
 * working state (apply is copy-by-value).
 *
 * Defined for the editor (increment 8); **not wired in increment
 * 4a.**
 *
 * REQ-AUTH-069; ADR-033 §4.1 / §12.
 */
export async function deleteProfile(profileId: string): Promise<void> {
  await apiFetch<void>(`/api/profiles/${encodeURIComponent(profileId)}`, {
    method: "DELETE",
  });
}

/**
 * Re-apply the shipped JSON for a built-in Global Profile
 * (``admin``, built-in only — HTTP 409 if the target is not a
 * built-in). Overwrites the stored document with the bundled
 * default.
 *
 * Defined for the admin reset affordance (increment 8); **not
 * wired in increment 4a.**
 *
 * REQ-AUTH-071; ADR-033 §12.
 */
export async function resetProfile(profileId: string): Promise<ProfileDetail> {
  return apiFetch<ProfileDetail>(`/api/profiles/${encodeURIComponent(profileId)}/reset`, {
    method: "POST",
  });
}
